import * as React from 'react'

import { clsx } from 'clsx'
import { AnimatePresence, motion } from 'motion/react'

import { Button, Separator, SimpleSpinner, toast } from '@buffer-mono/popcorn'

import type { Channel, CommentData } from '~publish/gql/graphql'

import { CommentCard } from './CommentCard'
import type { FilterStatus } from '../CommentsFilters/types'
import { useCommentThread } from '../hooks/useCommentThread'

import styles from './CommentWrapper.module.css'

const CONTEXT_COMMENTS_ANIMATION = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.2, ease: 'easeOut' },
      opacity: { duration: 0.2 },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.2, ease: 'easeIn' },
      opacity: { duration: 0.2 },
    },
  },
} as const

const ExpandContextButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void
    loading: boolean
  }
>(({ onClick, loading }, ref) => {
  if (loading) {
    return (
      <div className={styles.spinnerWrapper}>
        <SimpleSpinner size="small" />
      </div>
    )
  }

  return (
    <Button
      ref={ref}
      variant="tertiary"
      onClick={onClick}
      className={styles.expandContextButton}
      size="small"
    >
      Show more replies
    </Button>
  )
})

ExpandContextButton.displayName = 'ExpandContextButton'

export const CommentWrapper = React.forwardRef<
  HTMLLIElement,
  {
    comment: CommentData
    channel: Channel
    currentStatusFilter?: FilterStatus
    isFirstRepliableComment?: boolean
    onExpandNextComment?: () => void
    onManualExpansion?: (commentId: string | null) => void
  }
>((props, ref): JSX.Element => {
  const {
    comment,
    channel,
    currentStatusFilter,
    isFirstRepliableComment = false,
    onExpandNextComment,
    onManualExpansion,
  } = props
  const [shouldAnimateOut, setShouldAnimateOut] = React.useState(false)
  const { getCommentThread } = useCommentThread()
  const hasParentComment = comment.parentId !== null
  const [isLoadingContext, setIsLoadingContext] = React.useState(false)
  const [contextComments, setContextComments] = React.useState<CommentData[]>(
    [],
  )
  const [showContext, setShowContext] = React.useState(false)

  const handleExpandContext = async (): Promise<void> => {
    if (showContext) {
      setShowContext(false)
      return
    }

    setIsLoadingContext(true)
    try {
      const { context } = await getCommentThread(comment.id)
      setContextComments(context)
      setShowContext(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching comment context:', error)
      toast.error('Failed to load comment context')
    } finally {
      setIsLoadingContext(false)
    }
  }

  const showContextButton = hasParentComment && !showContext
  const showContextComments = showContext && contextComments.length > 0
  const shouldGreyOut =
    comment.status === 'dismissed' || comment.status === 'replied'

  return (
    <AnimatePresence
      mode="sync"
      initial={false}
      onExitComplete={(): void => setShouldAnimateOut(false)}
    >
      <motion.li
        key={comment.id}
        ref={ref}
        initial={false}
        animate={{
          opacity: shouldAnimateOut ? 0 : 1,
          height: shouldAnimateOut ? 0 : 'auto',
          backgroundColor: shouldGreyOut
            ? 'var(--color-bg-xsubtle)'
            : 'transparent',
        }}
        exit={{ height: 0, opacity: 0, backgroundColor: 'transparent' }}
        transition={{
          height: { duration: 0.2, ease: 'easeInOut' },
          opacity: { duration: 0.2 },
          backgroundColor: { duration: 0.2 },
        }}
        layout="position"
      >
        <div className={clsx(styles.wrapper)}>
          {showContextButton && (
            <ExpandContextButton
              onClick={handleExpandContext}
              loading={isLoadingContext}
            />
          )}
          {showContextComments && (
            <motion.div
              {...CONTEXT_COMMENTS_ANIMATION}
              layout="position"
              style={{ overflow: 'hidden' }}
            >
              {contextComments.map((contextComment) => (
                <CommentCard
                  key={`context-${contextComment.id}`}
                  comment={contextComment}
                  channel={channel}
                  currentStatusFilter={currentStatusFilter}
                  setShouldAnimateOut={setShouldAnimateOut}
                  isContextComment={true}
                  onExpandNextComment={onExpandNextComment}
                />
              ))}
            </motion.div>
          )}
          <CommentCard
            key={`comment-${comment.id}`}
            comment={comment}
            channel={channel}
            currentStatusFilter={currentStatusFilter}
            setShouldAnimateOut={setShouldAnimateOut}
            isFirstRepliableComment={isFirstRepliableComment}
            onExpandNextComment={onExpandNextComment}
            onManualExpansion={onManualExpansion}
          />
        </div>
        <Separator className={styles.separator} />
      </motion.li>
    </AnimatePresence>
  )
})

CommentWrapper.displayName = 'CommentWrapper'
